import moment from 'moment';

/** CloudWatch-style bucket intervals supported by the consumption microservice. */
export const CONSUMPTION_INTERVAL_GROUPS = [
  {
    label: 'Automatic',
    options: [{ label: 'Auto (based on date range)', value: 'auto' }],
  },
  {
    label: 'Seconds',
    options: [
      { label: '15 seconds', value: '15s' },
      { label: '30 seconds', value: '30s' },
    ],
  },
  {
    label: 'Minutes',
    options: [
      { label: '1 minute', value: '1m' },
      { label: '5 minutes', value: '5m' },
      { label: '15 minutes', value: '15m' },
      { label: '30 minutes', value: '30m' },
    ],
  },
  {
    label: 'Hours',
    options: [
      { label: '1 hour', value: '1h' },
      { label: '3 hours', value: '3h' },
      { label: '6 hours', value: '6h' },
      { label: '12 hours', value: '12h' },
    ],
  },
  {
    label: 'Days & weeks',
    options: [
      { label: '1 day', value: '1d' },
      { label: '1 week', value: '1w' },
    ],
  },
];

export const CONSUMPTION_INTERVAL_OPTIONS = CONSUMPTION_INTERVAL_GROUPS.reduce(
  (options, group) => options.concat(group.options),
  [],
);

const INTERVAL_SECONDS = {
  '15s': 15,
  '30s': 30,
  '1m': 60,
  '5m': 300,
  '15m': 900,
  '30m': 1800,
  '1h': 3600,
  '3h': 10800,
  '6h': 21600,
  '12h': 43200,
  '1d': 86400,
  '1w': 604800,
};

const INTERVAL_SHORT_LABELS = {
  auto: 'Auto',
  '15s': '15s',
  '30s': '30s',
  '1m': '1m',
  '5m': '5m',
  '15m': '15m',
  '30m': '30m',
  '1h': '1h',
  '3h': '3h',
  '6h': '6h',
  '12h': '12h',
  '1d': '1 day',
  '1w': '1 week',
};

const optionByValue = CONSUMPTION_INTERVAL_OPTIONS.reduce((map, option) => {
  map[option.value] = option;
  return map;
}, {});

/** Resolves bucket size in seconds (mirrors consumption microservice auto logic). */
export const resolveIntervalSeconds = (interval, from, to) => {
  const fromSeconds = moment(from).unix();
  const toSeconds = moment(to).unix();
  const rangeSeconds = Math.max(0, toSeconds - fromSeconds);

  if (interval && interval !== 'auto') {
    return INTERVAL_SECONDS[interval] ?? null;
  }

  if (rangeSeconds <= 3600) return 60;
  if (rangeSeconds <= 86400) return 3600;
  if (rangeSeconds <= 604800) return 86400;
  if (rangeSeconds <= 2592000) return 86400;
  return 604800;
};

export const getIntervalLabel = (value, short = false) => {
  if (short && INTERVAL_SHORT_LABELS[value]) {
    return INTERVAL_SHORT_LABELS[value];
  }
  return optionByValue[value]?.label ?? INTERVAL_SHORT_LABELS[value] ?? value;
};

const secondsToShortLabel = seconds => {
  const entry = Object.entries(INTERVAL_SECONDS).find(([, value]) => value === seconds);
  return entry ? getIntervalLabel(entry[0], true) : `${seconds}s`;
};

const ONE_DAY_SECONDS = 24 * 3600;
const ONE_WEEK_SECONDS = 7 * 86400;

export const HOURLY_ROLLUP_RETENTION_DAYS = 180;

export const isBeforeHourlyRetention = from => {
  const fromMoment = moment(from);
  return (
    fromMoment.isValid() &&
    fromMoment.isBefore(moment().subtract(HOURLY_ROLLUP_RETENTION_DAYS, 'days'))
  );
};

/**
 * Filters the interval groups shown in the "More intervals…" select based on range:
 * - Seconds & Minutes only appear when the range is 1 day or less.
 * - Hours only appear when the range is 1 week or less.
 */
export const getIntervalGroups = (from, to, groups = CONSUMPTION_INTERVAL_GROUPS) => {
  const rangeSeconds = Math.max(0, moment(to).diff(moment(from), 'seconds'));
  const showSubHour = rangeSeconds <= ONE_DAY_SECONDS;
  const showHours = rangeSeconds <= ONE_WEEK_SECONDS;

  if (isBeforeHourlyRetention(from)) {
    return groups
      .map(group => ({
        ...group,
        options: group.options.filter(option => option.value === '1w'),
      }))
      .filter(group => group.options.length > 0);
  }

  return groups.filter(group => {
    if (group.label === 'Seconds' || group.label === 'Minutes') {
      return showSubHour;
    }
    if (group.label === 'Hours') {
      return showHours;
    }
    return true;
  });
};

export const getQuickIntervalValues = (from, to) => {
  const rangeSeconds = Math.max(0, moment(to).diff(moment(from), 'seconds'));

  // Only weekly buckets read the daily rollup, which is the only tier that still
  // has data this far back.
  if (isBeforeHourlyRetention(from)) {
    return ['1w'];
  }

  if (rangeSeconds <= 2 * 3600) {
    return ['auto', '1m', '5m', '15m', '1h'];
  }
  if (rangeSeconds <= 24 * 3600) {
    return ['auto', '15m', '1h', '6h', '1d'];
  }
  if (rangeSeconds <= 7 * 86400) {
    return ['auto', '1h', '6h', '1d'];
  }
  if (rangeSeconds <= 60 * 86400) {
    return ['auto', '1d', '1w'];
  }
  return ['auto', '1d', '1w'];
};

/** Ranges over 2 months hit S3-backed queries; restrict to suggested intervals only. */
export const LONG_CONSUMPTION_RANGE_MONTHS = 2;

export const isConsumptionRangeOverTwoMonths = (from, to) => {
  const fromMoment = moment(from);
  const toMoment = moment(to);

  if (!fromMoment.isValid() || !toMoment.isValid()) {
    return false;
  }

  return toMoment.diff(fromMoment, 'months', true) > LONG_CONSUMPTION_RANGE_MONTHS;
};

export const formatRangeDuration = (from, to) => {
  const duration = moment.duration(moment(to).diff(moment(from)));
  const days = Math.floor(duration.asDays());
  const hours = duration.hours();
  const minutes = duration.minutes();

  if (days > 0) {
    return hours > 0
      ? `${days} day${days === 1 ? '' : 's'} ${hours} hr`
      : `${days} day${days === 1 ? '' : 's'}`;
  }
  if (hours > 0) {
    return minutes > 0 ? `${hours} hr ${minutes} min` : `${hours} hr`;
  }
  if (minutes > 0) {
    return `${minutes} min`;
  }
  return 'Less than 1 min';
};

export const estimateBucketCount = (from, to, interval) => {
  const bucketSeconds = resolveIntervalSeconds(interval, from, to);
  if (!bucketSeconds) return null;
  const rangeSeconds = Math.max(0, moment(to).unix() - moment(from).unix());
  return Math.max(1, Math.ceil(rangeSeconds / bucketSeconds));
};

export const buildRangeSummary = (from, to, interval) => {
  const durationLabel = formatRangeDuration(from, to);
  const bucketSeconds = resolveIntervalSeconds(interval, from, to);
  const intervalLabel =
    interval === 'auto'
      ? `Auto (${secondsToShortLabel(bucketSeconds)})`
      : getIntervalLabel(interval);
  const buckets = estimateBucketCount(from, to, interval);

  return {
    durationLabel,
    intervalLabel,
    buckets,
    text: `${durationLabel} · ${intervalLabel} buckets · ~${buckets} chart point${
      buckets === 1 ? '' : 's'
    }`,
  };
};
